/** @jsxImportSource @emotion/react */
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react'
import { css, keyframes } from '@emotion/react'
import { Tour } from 'antd'
import type { TourStepProps } from 'antd'
import colors from '@sportninja/common/constants/appColors'

interface SchedulerAdminTutorialProps {
  open: boolean
  onClose: () => void
}

// ─── Styles ──────────────────────────────────────────────────

const panelStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 12px;
  padding: 20px;
  max-width: 380px;
  font-family: 'Inter', sans-serif;
`

const welcomePanelStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 12px;
  padding: 24px;
  max-width: 420px;
  font-family: 'Inter', sans-serif;
`

const lastStepPanelStyles = css`
  background-color: ${colors.SECONDARY_400};
  border: 1px solid ${colors.SECONDARY_200};
  border-radius: 12px;
  padding: 24px;
  max-width: 440px;
  font-family: 'Inter', sans-serif;
`

const titleStyles = css`
  color: ${colors.NEUTRAL_0};
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 8px 0;
`

const descStyles = css`
  color: ${colors.NEUTRAL_50};
  font-size: 13px;
  line-height: 1.5;
  margin: 0 0 16px 0;
`

const dotsContainerStyles = css`
  display: flex;
  gap: 6px;
  margin-bottom: 16px;
`

const buttonRowStyles = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`

const skipBtnStyles = css`
  background: transparent;
  border: none;
  color: ${colors.NEUTRAL_100};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  cursor: pointer;
  padding: 6px 12px;
  margin-right: auto;

  &:hover {
    color: ${colors.NEUTRAL_50};
  }
`

const backBtnStyles = css`
  background: transparent;
  border: 1px solid ${colors.NEUTRAL_500};
  color: ${colors.NEUTRAL_50};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  padding: 6px 16px;
  border-radius: 6px;

  &:hover {
    border-color: ${colors.NEUTRAL_100};
    color: ${colors.NEUTRAL_0};
  }
`

const nextBtnStyles = css`
  background-color: ${colors.PRIMARY_300};
  border: none;
  color: ${colors.SECONDARY_500};
  font-family: 'Inter', sans-serif;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 6px 16px;
  border-radius: 6px;

  &:hover {
    background-color: ${colors.PRIMARY_200};
  }
`

const glowPulse = keyframes`
  0%, 100% { opacity: 0.7; }
  50% { opacity: 1; }
`

const welcomeIconContainerStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  margin: 0 auto 16px auto;
  border-radius: 50%;
  background: radial-gradient(
    circle,
    rgba(242, 180, 0, 0.15) 0%,
    transparent 70%
  );
  animation: ${glowPulse} 2s ease-in-out infinite;
`

const linkStyles = css`
  color: ${colors.PRIMARY_300};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    color: ${colors.PRIMARY_200};
    text-decoration: underline;
  }
`

const stepListStyles = css`
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const stepItemStyles = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 1.5;
  color: ${colors.NEUTRAL_50};
`

const stepBadgeStyles = css`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: ${colors.SECONDARY_200};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  color: ${colors.NEUTRAL_0};
  margin-top: 1px;
`

const lastStepSectionTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  line-height: 1.6;
  color: ${colors.NEUTRAL_50};
  margin: 0;
`

// ─── Constants ───────────────────────────────────────────────

const TOTAL_STEPS = 5

// ─── Helpers ─────────────────────────────────────────────────

const queryTarget = (selector: string): (() => HTMLElement) => {
  return () => {
    const el = document.querySelector(selector)
    return (el as HTMLElement) || document.body
  }
}

// ─── Welcome Illustration ────────────────────────────────────

const WelcomeIllustration: React.FC = () => (
  <svg
    width='48'
    height='48'
    viewBox='0 0 48 48'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <rect
      x='6'
      y='10'
      width='30'
      height='28'
      rx='3'
      stroke={colors.NEUTRAL_50}
      strokeWidth='2'
      fill='none'
    />
    <path d='M6 16H36' stroke={colors.NEUTRAL_50} strokeWidth='2' />
    <path
      d='M14 7V13'
      stroke={colors.NEUTRAL_50}
      strokeWidth='2'
      strokeLinecap='round'
    />
    <path
      d='M28 7V13'
      stroke={colors.NEUTRAL_50}
      strokeWidth='2'
      strokeLinecap='round'
    />
    <circle cx='14' cy='23' r='1.5' fill={colors.NEUTRAL_50} />
    <circle cx='21' cy='23' r='1.5' fill={colors.NEUTRAL_50} />
    <circle cx='28' cy='23' r='1.5' fill={colors.NEUTRAL_50} />
    <circle cx='14' cy='30' r='1.5' fill={colors.NEUTRAL_50} />
    <circle cx='21' cy='30' r='1.5' fill={colors.PRIMARY_300} />
    <circle cx='28' cy='30' r='1.5' fill={colors.NEUTRAL_50} />
    <path
      d='M40 8L41.5 12.5L46 14L41.5 15.5L40 20L38.5 15.5L34 14L38.5 12.5Z'
      fill={colors.PRIMARY_300}
    />
    <path
      d='M44 24L44.8 26.2L47 27L44.8 27.8L44 30L43.2 27.8L41 27L43.2 26.2Z'
      fill={colors.PRIMARY_300}
      opacity='0.6'
    />
  </svg>
)

// ─── Dots Component ──────────────────────────────────────────

const StepDots: React.FC<{ stepIndex: number }> = ({ stepIndex }) => (
  <div css={dotsContainerStyles}>
    {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
      <span
        key={i}
        css={css`
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: ${i === stepIndex
            ? colors.PRIMARY_300
            : colors.NEUTRAL_500};
          transition: background-color 0.2s ease;
        `}
      />
    ))}
  </div>
)

// ─── Step 0: Welcome ─────────────────────────────────────────

interface StepPanelProps {
  stepIndex: number
  onSkip: () => void
  onBack: () => void
  onNext: () => void
}

const WelcomePanel: React.FC<StepPanelProps> = ({
  onSkip,
  onNext,
  stepIndex,
}) => (
  <div css={welcomePanelStyles}>
    <div css={welcomeIconContainerStyles}>
      <WelcomeIllustration />
    </div>
    <h3 css={titleStyles}>Welcome to the Schedule Builder</h3>
    <p css={descStyles}>
      Build your game schedule in four simple steps: set up locations & time
      slots, organize divisions & teams, fine-tune constraints, then generate
      and review. Let&rsquo;s take a quick tour.
    </p>
    <StepDots stepIndex={stepIndex} />
    <div css={buttonRowStyles}>
      <button type='button' css={skipBtnStyles} onClick={onSkip}>
        Skip
      </button>
      <button type='button' css={nextBtnStyles} onClick={onNext}>
        Next
      </button>
    </div>
  </div>
)

// ─── Step 1: Workflow (highlights stepper) ───────────────────

const WorkflowPanel: React.FC<StepPanelProps> = ({
  stepIndex,
  onSkip,
  onBack,
  onNext,
}) => (
  <div css={panelStyles}>
    <h3 css={titleStyles}>Your 4-Step Workflow</h3>
    <p
      css={css`
        ${descStyles};
        margin-bottom: 8px;
      `}
    >
      Follow these tabs left to right. Jump between steps anytime.
    </p>
    <ol css={stepListStyles}>
      <li css={stepItemStyles}>
        <span css={stepBadgeStyles}>1</span>
        <span>
          <strong
            css={css`
              color: ${colors.NEUTRAL_0};
            `}
          >
            Locations & Time
          </strong>{' '}
          &mdash; Add venues, surfaces, and available time slots.
        </span>
      </li>
      <li css={stepItemStyles}>
        <span css={stepBadgeStyles}>2</span>
        <span>
          <strong
            css={css`
              color: ${colors.NEUTRAL_0};
            `}
          >
            Divisions & Teams
          </strong>{' '}
          &mdash; Organize divisions and assign teams.
        </span>
      </li>
      <li css={stepItemStyles}>
        <span css={stepBadgeStyles}>3</span>
        <span>
          <strong
            css={css`
              color: ${colors.NEUTRAL_0};
            `}
          >
            Global Constraints
          </strong>{' '}
          &mdash; Set rules like rest days, blackouts, and limits.
        </span>
      </li>
      <li css={stepItemStyles}>
        <span css={stepBadgeStyles}>4</span>
        <span>
          <strong
            css={css`
              color: ${colors.NEUTRAL_0};
            `}
          >
            Review Schedule
          </strong>{' '}
          &mdash; Generate, review, and publish your schedule.
        </span>
      </li>
    </ol>
    <div
      css={css`
        margin-top: 16px;
      `}
    >
      <StepDots stepIndex={stepIndex} />
      <div css={buttonRowStyles}>
        <button type='button' css={skipBtnStyles} onClick={onSkip}>
          Skip
        </button>
        <button type='button' css={backBtnStyles} onClick={onBack}>
          Back
        </button>
        <button type='button' css={nextBtnStyles} onClick={onNext}>
          Next
        </button>
      </div>
    </div>
  </div>
)

// ─── Step 2: Flights (highlights date range section) ─────────

const FlightsPanel: React.FC<StepPanelProps> = ({
  stepIndex,
  onSkip,
  onBack,
  onNext,
}) => (
  <div css={panelStyles}>
    <h3 css={titleStyles}>Schedule Flights</h3>
    <p css={descStyles}>
      Flights divide your season into separate scheduling periods. Each flight
      has its own date range, time slots, and generated schedule &mdash; think
      of them as phases of your season.
    </p>
    <p
      css={css`
        ${descStyles};
        margin-top: -8px;
      `}
    >
      For example, use Flight 1 for the fall season (Sep&ndash;Dec) and Flight 2
      for winter (Jan&ndash;Mar), each with different venues or time slots.
    </p>
    <p
      css={css`
        ${descStyles};
        margin-top: -8px;
        font-style: italic;
      `}
    >
      Note: If you&apos;re generating your full season schedule at once, you
      only need one flight.
    </p>
    <StepDots stepIndex={stepIndex} />
    <div css={buttonRowStyles}>
      <button type='button' css={skipBtnStyles} onClick={onSkip}>
        Skip
      </button>
      <button type='button' css={backBtnStyles} onClick={onBack}>
        Back
      </button>
      <button type='button' css={nextBtnStyles} onClick={onNext}>
        Next
      </button>
    </div>
  </div>
)

// ─── Step 3: Generate (highlights button) ────────────────────

const GeneratePanel: React.FC<StepPanelProps> = ({
  stepIndex,
  onSkip,
  onBack,
  onNext,
}) => (
  <div css={panelStyles}>
    <h3 css={titleStyles}>Generate Your Schedule</h3>
    <p css={descStyles}>
      When everything is set, hit Generate. The scheduler will build an
      optimized game schedule from your configuration. Review the results,
      resolve conflicts, then publish.
    </p>
    <p
      css={css`
        ${descStyles};
        margin-top: -8px;
      `}
    >
      You can tweak your constraints, priority order, or other settings at any
      time and re-generate.
    </p>
    <StepDots stepIndex={stepIndex} />
    <div css={buttonRowStyles}>
      <button type='button' css={skipBtnStyles} onClick={onSkip}>
        Skip
      </button>
      <button type='button' css={backBtnStyles} onClick={onBack}>
        Back
      </button>
      <button type='button' css={nextBtnStyles} onClick={onNext}>
        Next
      </button>
    </div>
  </div>
)

// ─── Step 4: Resources (centered, last step) ─────────────────

const ResourcesPanel: React.FC<StepPanelProps> = ({
  stepIndex,
  onBack,
  onNext,
}) => (
  <div css={lastStepPanelStyles}>
    <h3 css={titleStyles}>Need More Help?</h3>
    <p css={lastStepSectionTextStyles}>
      For a detailed walkthrough with screenshots and tips, visit our{' '}
      <a
        href='https://support.sportninja.com/schedule-creation'
        target='_blank'
        rel='noopener noreferrer'
        css={linkStyles}
      >
        Schedule Creation Guide
      </a>
      .
    </p>
    <p
      css={css`
        ${lastStepSectionTextStyles};
        margin-top: 8px;
      `}
    >
      You can reopen this tour anytime by clicking the{' '}
      <strong
        css={css`
          color: ${colors.NEUTRAL_0};
        `}
      >
        info icon
      </strong>{' '}
      in the top-right corner.
    </p>

    <div
      css={css`
        margin-top: 16px;
      `}
    >
      <StepDots stepIndex={stepIndex} />
      <div css={buttonRowStyles}>
        <button type='button' css={backBtnStyles} onClick={onBack}>
          Back
        </button>
        <button type='button' css={nextBtnStyles} onClick={onNext}>
          Got it
        </button>
      </div>
    </div>
  </div>
)

// ─── Main Component ──────────────────────────────────────────

const SchedulerAdminTutorial: React.FC<SchedulerAdminTutorialProps> = ({
  open,
  onClose,
}) => {
  const [current, setCurrent] = useState(0)
  const centerAnchorRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (open) {
      setCurrent(0)
    }
  }, [open])

  const handleNext = useCallback(() => {
    if (current < TOTAL_STEPS - 1) {
      setCurrent((prev) => prev + 1)
    } else {
      onClose()
    }
  }, [current, onClose])

  const handleBack = useCallback(() => {
    if (current > 0) {
      setCurrent((prev) => prev - 1)
    }
  }, [current])

  const panelProps = useMemo(
    () => ({
      onSkip: onClose,
      onBack: handleBack,
      onNext: handleNext,
    }),
    [onClose, handleBack, handleNext]
  )

  const steps: TourStepProps[] = useMemo(
    () => [
      {
        title: '',
        description: '',
        target: () => centerAnchorRef.current as HTMLElement,
        placement: 'bottom' as const,
        renderPanel: () => <WelcomePanel stepIndex={0} {...panelProps} />,
      },
      {
        title: '',
        description: '',
        target: queryTarget('[data-tour-stepper]'),
        placement: 'bottom' as const,
        renderPanel: () => <WorkflowPanel stepIndex={1} {...panelProps} />,
      },
      {
        title: '',
        description: '',
        target: queryTarget('[data-tour-flight-range]'),
        placement: 'bottom' as const,
        renderPanel: () => <FlightsPanel stepIndex={2} {...panelProps} />,
      },
      {
        title: '',
        description: '',
        target: queryTarget('[data-tour-generate-btn]'),
        placement: 'bottom' as const,
        renderPanel: () => <GeneratePanel stepIndex={3} {...panelProps} />,
      },
      {
        title: '',
        description: '',
        target: () => centerAnchorRef.current as HTMLElement,
        placement: 'bottom' as const,
        renderPanel: () => <ResourcesPanel stepIndex={4} {...panelProps} />,
      },
    ],
    [panelProps]
  )

  return (
    <>
      <div
        ref={centerAnchorRef}
        css={css`
          position: fixed;
          top: 20%;
          left: 50%;
          width: 1px;
          height: 1px;
          pointer-events: none;
          z-index: -1;
        `}
      />
      <Tour
        open={open}
        current={current}
        onChange={setCurrent}
        onClose={onClose}
        steps={steps}
        mask={{ color: 'rgba(0, 0, 0, 0.65)' }}
        gap={{ offset: 8, radius: 8 }}
        animated
        zIndex={1050}
      />
    </>
  )
}

export default SchedulerAdminTutorial
